/* import __COLOCATED_TEMPLATE__ from './summary.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import type { Job } from 'embercom/components/reporting/automation/ai-insights/optimize/jobs';
import {
  loadInsightsSummary,
  type InsightsSummaryResult,
  type LoadInsightsSummaryArgs,
} from 'embercom/components/reporting/automation/ai-insights/conversation-summary-data-resources';
import { trackedTask } from 'ember-resources/util/ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import { restartableTask } from 'ember-concurrency-decorators';
import { inject as service } from '@ember/service';
import { type TaskGenerator } from 'ember-concurrency';
import type ReportingChartService from 'embercom/services/reporting-chart-service';
import type Range from 'embercom/models/reporting/range';
import { type FlexibleQueryTimeRange } from 'embercom/services/reporting-csv-export';
import type ReportingMetrics from 'embercom/services/reporting-metrics';
import type ReportingUnderlyingDataService from 'embercom/services/reporting-underlying-data-service';
import { and } from 'embercom/components/reporting/automation/ai-insights/fin-insights/sankey-config';
import { type LogicalFilter } from 'embercom/components/reporting/custom/filters';
import { type SummaryTab } from './conversation-explore-panel';
import { tracked } from '@glimmer/tracking';
import { convertMarkdownToHtml, extractSummaryCards, type SummaryCard } from './summary-converter';
import type ContentWrapper from 'embercom/models/content-service/content-wrapper';
import type IntlService from 'embercom/services/intl';
import type KnowledgeHubDrawerEditorService from 'embercom/services/knowledge-hub-drawer-editor-service';
import type Store from '@ember-data/store';

interface Signature {
  Args: {
    selectedJob: Job;
    attributeId: string;
    attributeValue: string;
    breakdownAttributeValue?: string;
    dateRange: Range;
    onShowConversationExplorer: (
      selectedConversationId: number | null,
      filters: LogicalFilter,
      attributeValuesToExplore: string[],
      summaryType: SummaryTab,
      summaryText: string,
      summaryHeading?: string,
      conversationIdsToLoad?: number[],
    ) => void;
    summaryType: SummaryTab;
    conversationCount?: number;
    hasSuggestions: boolean;
    suggestions?: ContentWrapper[];
    isCompleted?: (suggestion: ContentWrapper) => boolean;
  };
}

export default class Summary extends Component<Signature> {
  @service declare appService: AppService;
  @service declare reportingChartService: ReportingChartService;
  @service declare reportingMetrics: ReportingMetrics;
  @service declare reportingUnderlyingDataService: ReportingUnderlyingDataService;
  @service declare intl: IntlService;
  @service declare knowledgeHubDrawerEditorService: KnowledgeHubDrawerEditorService;
  @service declare store: Store;

  @tracked editingContentId: number | null = null;
  @tracked editingContentType: string | null = null;
  @tracked editingContentMode = 'review';

  fetchSummaryTask = taskFor(this.loadSummary);

  summaryData = trackedTask(this, this.fetchSummaryTask, () => [
    this.processedFilters,
    this.args.dateRange,
    this.args.attributeId,
  ]);

  get attributeLabel(): string {
    let attributeValue = this.args.attributeValue;
    let attributeId = this.args.attributeId;
    if (this.args.breakdownAttributeValue) {
      if (
        this.args.selectedJob.breakdownByAttributeId ===
        'conversation_custom_fields#ai_insights_topic'
      ) {
        return this.args.breakdownAttributeValue;
      } else {
        attributeValue = this.args.breakdownAttributeValue;
        attributeId = this.args.selectedJob.breakdownByAttributeId || '';
      }
    }

    let value = this.reportingUnderlyingDataService.mappedValueFor(attributeValue, attributeId);

    if (typeof value === 'string') {
      return value;
    } else if (value && typeof value === 'object') {
      return value.toString();
    }

    return this.args.attributeValue || '';
  }

  get timeRange(): FlexibleQueryTimeRange {
    return {
      start: this.args.dateRange.startMoment.valueOf(),
      end: this.args.dateRange.endMoment.valueOf(),
      interval: this.args.dateRange.interval,
      property: this.metric.timeProperty,
    };
  }

  get metric() {
    return this.reportingMetrics.getMetricById('v1.new_conversations');
  }

  get conversationCount() {
    return this.args.conversationCount ? this.args.conversationCount : null;
  }

  get processedFilters() {
    let filter = {
      type: 'category',
      data: {
        property: this.args.attributeId,
        attribute: this.args.attributeId,
        values: [this.args.attributeValue],
      },
    };
    if (this.args.selectedJob.filters) {
      return and(this.args.selectedJob.filters, filter, this.breakdownFilter);
    } else {
      return and(filter, this.breakdownFilter);
    }
  }

  get breakdownFilter() {
    if (!this.args.breakdownAttributeValue) {
      return undefined;
    }
    if (
      this.args.selectedJob.breakdownByAttributeId ===
      'conversation_custom_fields#ai_insights_topic_subtopic'
    ) {
      return {
        type: 'category',
        data: {
          property: 'conversation_custom_fields#ai_insights_subtopic',
          attribute: 'conversation_custom_fields#ai_insights_subtopic',
          values: [this.args.breakdownAttributeValue.split('=>')[1]],
        },
      };
    } else {
      return {
        type: 'category',
        data: {
          property: this.args.selectedJob.breakdownByAttributeId,
          attribute: this.args.selectedJob.breakdownByAttributeId,
          values: [this.args.breakdownAttributeValue],
        },
      };
    }
  }

  get summaryText(): string {
    return this.fetchSummaryTask.lastSuccessful?.value?.summaryText || '';
  }

  get processedSummaryText(): SummaryCard {
    if (this.args.summaryType === 'resolution_rate_drivers') {
      let extracted = extractSummaryCards(this.summaryText)[0] || {
        summary_title: '',
        summary_text: '',
      };
      return extracted;
    } else {
      let htmlContent = convertMarkdownToHtml(this.summaryText, false, false);
      let result = {
        summary_title: this.attributeLabel,
        summary_text: htmlContent,
      };
      return result;
    }
  }

  @restartableTask
  *loadSummary(): TaskGenerator<InsightsSummaryResult | null> {
    let params = {
      appId: this.appService.app.id,
      adminId: this.appService.app.currentAdmin.id,
      filter: this.processedFilters,
      timeRange: this.timeRange,
      sortByAttributeId: 'conversation.first_user_conversation_part_created_at',
      sortDirection: 'desc',
      syntheticDataUseCase: this.reportingChartService.syntheticDataUseCase,
      timezone: this.appService.app.timezone,
      summaryType: this.args.summaryType,
      selectedCxAttribute: this.args.attributeValue,
      maxConversationCount: 5000,
    } as LoadInsightsSummaryArgs;

    if (this.args.summaryType === 'resolution_rate_drivers') {
      params.escalationReason = this.args.attributeValue;
      params.topicName = this.args.breakdownAttributeValue;
    }

    return yield loadInsightsSummary(params);
  }

  get subtopicSuggestions(): ContentWrapper[] {
    return this.args.suggestions || [];
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::Optimize::Summary': typeof Summary;
    'reporting/automation/ai-insights/optimize/summary': typeof Summary;
  }
}
